import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
  padding: 2.25rem 1.5rem 1.9rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  color: rgb(var(--fg));
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 20% 20%, rgba(94, 234, 212, 0.15), transparent 45%),
      radial-gradient(circle at 82% 22%, rgba(94, 234, 212, 0.08), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 1.9rem 1rem 1.5rem;
    margin-bottom: 32px;
    border-radius: 18px;
  }
`;

const Title = styled.h1`
  font-size: 2.7rem;
  color: rgb(var(--fg) / 0.95);
  margin: 0;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.03em;

  @media (max-width: 600px) {
    font-size: 2.1rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 1.15rem;
  font-weight: 500;
  color: rgb(var(--fg) / 0.78);
  margin: 10px 0 12px;
  font-style: italic;
  line-height: 1.6;

  @media (max-width: 600px) {
    font-size: 1rem;
  }
`;

const DatePublished = styled.p`
  font-size: 0.9rem;
  color: rgb(var(--gray) / 0.7);
  margin: 0 0 20px;
  letter-spacing: 0.02em;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 0;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  font-size: 0.82rem;
  padding: 7px 14px;
  border-radius: 999px;
  font-weight: 600;
  user-select: none;
  border: 1px solid rgba(94, 234, 212, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.08);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
  padding: 1.6rem 1.45rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 600px) {
    margin-bottom: 1.25rem;
    padding: 1.2rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.45rem;
  color: #5eead4;
  margin-bottom: 16px;
  border-bottom: 1px solid rgba(94, 234, 212, 0.25);
  padding-bottom: 10px;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.2rem;
    margin-bottom: 12px;
    padding-bottom: 8px;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin: 0 0 1rem 0;

  @media (max-width: 600px) {
    font-size: 0.95rem;
    line-height: 1.75;
  }
`;

const List = styled.ul`
  margin: 0 0 1rem 0;
  padding-left: 1.4rem;
  color: rgb(var(--fg) / 0.82);
`;

const ListItem = styled.li`
  font-size: 1rem;
  line-height: 1.85;
  margin-bottom: 0.35rem;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-top: 0.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InnerCard = styled.div`
  background: rgb(var(--fg) / 0.045);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 14px;
  padding: 1.1rem 1.2rem;
`;

const InnerCardTitle = styled.h4`
  font-size: 0.95rem;
  font-weight: 700;
  color: #5eead4;
  margin: 0 0 0.55rem 0;
  letter-spacing: -0.01em;
`;

const CodeBlock = styled.pre`
  background: rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  color: rgb(var(--fg) / 0.86);
  padding: 1.15rem;
  border-radius: 16px;
  font-family: 'Source Code Pro', monospace, monospace;
  font-size: 0.88rem;
  line-height: 1.65;
  overflow-x: auto;
  margin: 20px 0;
  border: 1px solid rgb(var(--fg) / 0.06);
  box-shadow: 0 16px 36px rgb(0 0 0 / calc(0.35 * var(--shadow-mul)));

  @media (max-width: 600px) {
    font-size: 0.8rem;
    padding: 0.95rem;
    border-radius: 14px;
  }
`;

const BackButtonWrapper = styled.div`
  text-align: center;
  margin-top: 28px;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  user-select: none;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(94, 234, 212, 0.5);
  }

  @media (max-width: 600px) {
    width: 100%;
    padding: 0.85rem 1.5rem;
    font-size: 0.78rem;
  }
`;

export const MeetingPlannerMetadata = {
  title: 'Meeting Planner',
  description: 'A group scheduling tool where organizers create a meeting page, share a link, and guests mark their weekly availability on an interactive drag-select grid.',
  tags: ['React', 'Supabase', 'Full Stack', 'UX Design'],
  languages: ['JavaScript', 'React', 'Vercel', 'Supabase'],
  datePublished: '2026-03-01',
  coverImage: '/images/meeting_planner.png',
  path: '/projects/meeting-planner',
};

const MeetingPlanner = () => {
  const navigate = useNavigate();
  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>Meeting Planner</Title>
          <Subtitle>Group Scheduling Tool with Drag-Select Availability</Subtitle>
          <DatePublished>Published on March 1, 2026</DatePublished>
          <CoverImage src={MeetingPlannerMetadata.coverImage} alt="Meeting Planner Cover" />
          <TagsContainer>
            {MeetingPlannerMetadata.tags.map(tag => (
              <Tag key={tag}>{tag}</Tag>
            ))}
          </TagsContainer>
        </Header>

        <Section>
          <SectionTitle>Overview</SectionTitle>
          <Paragraph>
            I built this because I got tired of the back-and-forth that happens every time a small group tries to find a meeting time. The usual flow is someone sends a message asking "when works for everyone?", five people reply with different formats, and someone has to manually cross-reference everything. Tools like Doodle and When2meet exist, but I wanted something that felt more modern and worked the way I wanted it to.
          </Paragraph>
          <Paragraph>
            Meeting Planner lets an organizer create a meeting page for a specific week, share a link, and have guests mark their availability on a drag-select grid. The organizer then sees a heatmap of the group's overlap and gets automatic suggestions for the best times. Only the organizer needs an account. Guests just open the link, fill in their name, select their slots, and submit.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Features</SectionTitle>
          <TwoColumn>
            <InnerCard>
              <InnerCardTitle>Drag-Select Availability Grid</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                A Monday to Friday grid from 8am to 8pm, with configurable 30 or 60 minute slots. Guests click and drag across cells to mark when they're free. It's much faster than toggling individual slots one by one.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Group Availability Heatmap</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                As responses come in, the organizer sees the same grid as a color-coded heatmap. Gray means nobody is free, green means everyone is. Each cell shows the count so you can tell at a glance where the overlap is.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Best-Time Suggestions</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                The app surfaces three suggestions automatically: the slot where everyone is free, the slot with the highest attendance, and the best consecutive one-hour window. The organizer can finalize a time directly from these suggestions.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Guest Access via Link</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Each meeting gets a unique share link. Guests open it, enter their name and optionally their email, mark their slots, and submit. If they come back later from the same browser, their previous response is pre-filled so they can update it.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Meeting Finalization</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Once enough responses are in, the organizer picks a time and confirms it. The finalized slot is saved and displayed on the meeting page so everyone sees the same answer.
              </Paragraph>
            </InnerCard>
            <InnerCard>
              <InnerCardTitle>Organizer Dashboard</InnerCardTitle>
              <Paragraph style={{ margin: 0, fontSize: '0.95rem' }}>
                Organizers have a dashboard listing all their meetings with title, week, and status. They can copy the share link, edit meeting details like location or agenda, or delete meetings they no longer need.
              </Paragraph>
            </InnerCard>
          </TwoColumn>
        </Section>

        <Section>
          <SectionTitle>Technical Breakdown</SectionTitle>
          <Paragraph>
            The frontend is React 19 with Vite and Tailwind CSS. The backend is entirely Supabase: PostgreSQL for the database, Supabase Auth for organizer accounts, and Row-Level Security policies to control access at the database level. Guest submissions go through a Supabase Edge Function that verifies a Cloudflare Turnstile CAPTCHA before writing anything. The app is hosted on Vercel.
          </Paragraph>
          <Paragraph>
            There is no custom backend server. The frontend talks directly to Supabase, and RLS policies handle authorization. Guests never touch the tables directly. All their reads and writes go through SECURITY DEFINER RPC functions scoped to the meeting's share token, so the organizer's ID and other guests' emails are never exposed.
          </Paragraph>
          <CodeBlock>{`profiles          → organizer display names
meetings          → title, week, duration, share token, agenda, location
meeting_responses → one row per guest (name, email, guest token, edit token)
response_slots    → one row per selected time slot per response`}</CodeBlock>
          <Paragraph>
            The overlap computation runs on the client. It collects all the slot timestamps across responses, counts how many people selected each one, and produces the three suggestion types (full overlap, best attendance, best one-hour window). The heatmap is just a visual layer on top of those same counts.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Scope Decisions</SectionTitle>
          <List>
            <ListItem>
              <strong>Weekly scheduling only.</strong> The tool answers one question: "what time works for our group this week?" I kept recurring schedules, multi-week views, and timezone handling out of scope. The target use case is informal groups that are mostly in the same area.
            </ListItem>
            <ListItem>
              <strong>Guests don't need accounts.</strong> Requiring sign-up would add friction for something that should take 30 seconds. Guests are identified by a token stored in their browser, and optionally by email if they want a recovery link to edit their response later.
            </ListItem>
            <ListItem>
              <strong>Rate limiting and spam protection.</strong> Guest submissions are capped at 5 per name per 10 minutes per meeting, and each meeting accepts up to 100 responses. Cloudflare Turnstile handles bot prevention on the guest form. Organizers are limited to 10 meetings per week.
            </ListItem>
            <ListItem>
              <strong>No email notifications yet.</strong> The organizer shares the link manually. Transactional email is the natural next feature, but it wasn't necessary for the core flow to work.
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>What I Learned</SectionTitle>
          <Paragraph>
            The drag-select grid was the trickiest part. Managing drag state across a two-dimensional cell layout, tracking which cells the cursor passes through, and making the interaction feel smooth took more iteration than I expected. It's a small detail, but it's the main thing people interact with, so it had to feel right.
          </Paragraph>
          <Paragraph>
            Working with Supabase RLS was also a good learning experience. The security model is powerful but requires you to think carefully about who can see what at the query level. Using SECURITY DEFINER functions to scope guest access to just the share token was the cleanest way to keep guest data separated without building a separate API layer.
          </Paragraph>
          <Paragraph>
            I built this over a weekend with the help of Claude Code. The parts that would normally slow a solo project down, like the schema design, wiring up RLS policies, and debugging edge cases in the overlap logic, moved quickly. It went from idea to deployed product in about two days.
          </Paragraph>
        </Section>

        <BackButtonWrapper>
          <BackButton onClick={() => navigate('/projects')}>Back to Projects</BackButton>
        </BackButtonWrapper>
      </ContentWrapper>
    </Container>
  );
};

export default MeetingPlanner;
